import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import Breadcrumbs from '../../components/Breadcrumbs';
import AdvancedOptions from './components/AdvancedOptions';
import { defaultCompressionOptions } from '../../utils/helpers';
import { getPresets, createPreset, updatePreset, deletePreset, setDefaultPreset, removeDefaultPreset } from '../../api/presets';
import { trackPresetCreate } from '../../utils/analytics';
import { PageHeader, Card, CardHeader, CardContent, Button } from '../ui';

export default function Presets() {
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingOptions, setEditingOptions] = useState(defaultCompressionOptions);
  const [presetName, setPresetName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const crumbs = [
    { label: 'Dashboard', to: '/app/dashboard' },
    { label: 'Presets' }
  ];

  // Helper function to generate a unique default preset name
  const generateDefaultPresetName = () => {
    const baseName = 'Custom Preset';
    
    // Find existing preset names that start with the base name
    const existingNumbers = presets
      .map(preset => preset.name)
      .filter(name => name.startsWith(baseName))
      .map(name => {
        const match = name.match(new RegExp(`^${baseName}\\s*#?(\\d+)$`));
        return match ? parseInt(match[1], 10) : (name === baseName ? 1 : 0);
      })
      .filter(num => num > 0);

    // Find the next available number
    let nextNumber = 1;
    if (existingNumbers.length > 0) {
      const maxNumber = Math.max(...existingNumbers);
      nextNumber = maxNumber + 1;
    }

    return `${baseName} #${nextNumber}`;
  };

  const handleNestedOptionChange = (group, optionKey, value) => {
    setEditingOptions((prev) => ({
      ...prev,
      [group]: { ...prev[group], [optionKey]: value },
    }));
  };

  const handleCreatePreset = () => {
    setSelectedPreset(null);
    setPresetName(generateDefaultPresetName());
    setEditingOptions(defaultCompressionOptions);
    setIsEditing(true);
  };

  const handleEditPreset = (preset) => {
    setSelectedPreset(preset);
    setPresetName(preset.name);
    setEditingOptions(preset.options);
    setIsEditing(true);
  };

  const handleSavePreset = async () => {
    const finalPresetName = presetName.trim() || generateDefaultPresetName();

    try {
      setError('');
      setIsSaving(true);

      const presetData = {
        name: finalPresetName,
        options: editingOptions,
      };

      let savedPreset;
      if (selectedPreset) {
        // Update existing preset
        savedPreset = await updatePreset(selectedPreset.id, presetData);
        setPresets(presets.map(p => p.id === savedPreset.id ? savedPreset : p));
      } else {
        // Create new preset
        savedPreset = await createPreset(presetData);
        setPresets([...presets, savedPreset]);
        trackPresetCreate();
      }

      setIsEditing(false);
      setSelectedPreset(null);
    } catch (err) {
      setError(err.message || 'Failed to save preset');
      console.error('Save preset error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePreset = async (preset) => {
    if (!window.confirm(`Are you sure you want to delete the preset "${preset.name}"?`)) {
      return;
    }

    try {
      setError('');
      await deletePreset(preset.id);
      setPresets(presets.filter(p => p.id !== preset.id));
      if (selectedPreset?.id === preset.id) {
        setSelectedPreset(null);
        setIsEditing(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to delete preset');
      console.error('Delete preset error:', err);
    }
  };

  const handleSetDefault = async (preset) => {
    try {
      setError('');
      const updatedPreset = await setDefaultPreset(preset.id);
      setPresets(presets.map(p => ({
        ...p,
        is_default: p.id === updatedPreset.id
      })));
    } catch (err) {
      setError(err.message || 'Failed to set default preset');
      console.error('Set default preset error:', err);
    }
  };

  const handleRemoveDefault = async (preset) => {
    try {
      setError('');
      const updatedPreset = await removeDefaultPreset(preset.id);
      setPresets(presets.map(p => ({
        ...p,
        is_default: p.id === updatedPreset.id ? false : p.is_default
      })));
    } catch (err) {
      setError(err.message || 'Failed to remove default preset');
      console.error('Remove default preset error:', err);
    }
  };

  useEffect(() => {
    const loadPresets = async () => {
      try {
        setIsLoading(true);
        setError('');
        const response = await getPresets();
        setPresets(response.presets);
      } catch (err) {
        setError(err.message || 'Failed to load presets');
        console.error('Load presets error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadPresets();
  }, []);

  return (
    <div className="space-y-6">
      <Breadcrumbs crumbs={crumbs} />
      
      <PageHeader 
        title="Compression presets"
        subtitle="Create and manage your compression presets"
        actions={
          <Button
            onClick={handleCreatePreset}
            disabled={isEditing}
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            New preset
          </Button>
        }
      />

      {error && (
        <div className="p-4 bg-danger-soft/50 border border-danger-border/60 rounded-xl text-sm text-danger-soft-foreground flex items-start gap-3">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Presets List */}
        <Card>
          <CardHeader 
            title="Your presets"
            subtitle={presets.length > 0 ? `${presets.length} preset${presets.length !== 1 ? 's' : ''}` : null}
          />
          <CardContent>
            {isLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading presets...
              </div>
            ) : presets.length === 0 ? (
              <p className="text-sm text-muted-foreground">No presets yet. Create one to get started!</p>
            ) : (
              <div className="space-y-2">
                {presets.map((preset) => (
                <div
                  key={preset.id}
                  className={`flex items-center justify-between p-3 rounded-xl transition-all duration-200 cursor-pointer ${
                    selectedPreset?.id === preset.id
                      ? 'bg-accent text-accent-foreground shadow-sm'
                      : 'bg-background-soft hover:bg-background-elevated text-foreground border border-border/50 hover:border-border'
                  }`}
                  onClick={() => handleEditPreset(preset)}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="font-medium truncate">{preset.name}</span>
                    {preset.is_default && (
                      <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium bg-warning/10 text-warning rounded-md border border-warning/20">
                        Default
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (preset.is_default) {
                          handleRemoveDefault(preset);
                        } else {
                          handleSetDefault(preset);
                        }
                      }}
                      className={`p-1.5 rounded-lg transition-all duration-200 ${
                        preset.is_default
                          ? selectedPreset?.id === preset.id
                            ? 'bg-accent-foreground/10 text-accent-foreground hover:bg-accent-foreground/20'
                            : 'bg-warning/10 text-warning hover:bg-warning/20 border border-warning/20'
                          : selectedPreset?.id === preset.id
                            ? 'bg-accent-foreground/10 text-accent-foreground hover:bg-accent-foreground/20'
                            : 'bg-background text-muted-foreground hover:bg-warning/10 hover:text-warning hover:border-warning/20 border border-border/50'
                      }`}
                      title={preset.is_default ? "Remove default preset" : "Set as default preset"}
                      disabled={isSaving}
                    >
                      {preset.is_default ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePreset(preset);
                      }}
                      className={`p-1.5 rounded-lg transition-all duration-200 ${
                        selectedPreset?.id === preset.id
                          ? 'bg-accent-foreground/10 text-accent-foreground hover:bg-danger/20 hover:text-danger'
                          : 'bg-background text-muted-foreground hover:bg-danger/10 hover:text-danger hover:border-danger/20 border border-border/50'
                      }`}
                      title="Delete preset"
                      disabled={isSaving}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          </CardContent>
        </Card>

        {/* Preset Editor */}
        <div className="lg:col-span-2">
          {isEditing ? (
            <Card className="flex flex-col h-[calc(100vh-200px)]">
              <CardHeader 
                title={selectedPreset ? "Edit preset" : "New preset"}
                subtitle={selectedPreset ? `Editing ${selectedPreset.name}` : "Configure your compression settings"}
              />
              <CardContent className="flex-1 overflow-y-auto">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Preset name
                    </label>
                    <input
                      type="text"
                      value={presetName}
                      onChange={(e) => setPresetName(e.target.value)}
                      className="w-full bg-background-soft border border-border/60 rounded-lg px-3.5 py-2.5 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all"
                      placeholder="Enter preset name"
                      disabled={isSaving}
                    />
                  </div>

                  <AdvancedOptions
                    options={editingOptions}
                    onNestedChange={handleNestedOptionChange}
                  />
                </div>
              </CardContent>

              <div className="flex justify-between px-6 py-4 border-t border-border/50 bg-background-soft/30">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsEditing(false);
                    setSelectedPreset(null);
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <div className="flex gap-3">
                  {selectedPreset && !selectedPreset.is_default && (
                    <Button
                      variant="ghost"
                      onClick={() => handleSetDefault(selectedPreset)}
                      disabled={isSaving}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                      Set as default
                    </Button>
                  )}
                  <Button
                    onClick={handleSavePreset}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Save preset
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <Card className="flex items-center justify-center min-h-[400px]">
              <CardContent>
                <div className="text-center space-y-3">
                  <svg className="w-16 h-16 mx-auto text-muted-foreground/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <p className="text-sm text-muted-foreground">Select a preset to edit or create a new one</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}